import { useTranslation } from 'react-i18next'
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react'
import { Button } from '@/components/shadcn/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn/select'
import { Separator } from '@/components/shadcn/separator'

interface Props {
  offset: number
  limit: number
  totalCount?: number | null
  onOffsetChange: (offset: number) => void
  onPageSizeChange: (pageSize: number) => void
}

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50, 100]

export function PaginationFooter({ offset, limit, totalCount, onOffsetChange, onPageSizeChange }: Props) {
  const { t } = useTranslation()
  const total = totalCount ?? 0
  const totalPages = limit > 0 ? Math.ceil(total / limit) : 0
  const page = limit > 0 ? Math.floor(offset / limit) + 1 : 1
  const availablePages = Array.from({ length: Math.max(0, totalPages) }, (_, i) => i + 1)
  const hasPrevious = page > 1
  const hasNext = page < totalPages

  const goToPage = (targetPage: number) => onOffsetChange((targetPage - 1) * limit)

  return (
    <>
      <Separator />
      <div className="flex items-center justify-between bg-muted/5 p-2">
        <div className="flex items-center gap-1">
          <p className="text-sm font-medium">{t('pagination.show')}</p>
          <Select value={limit.toString()} onValueChange={(v) => onPageSizeChange(Number(v))}>
            <SelectTrigger className="w-auto h-8!">
              <SelectValue />
            </SelectTrigger>
            <SelectContent position="popper" side="top">
              {PAGE_SIZE_OPTIONS.map((size) => (
                <SelectItem key={size} value={size.toString()}>{size}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-sm text-muted-foreground">{t('pagination.of')} {total}</p>
        </div>

        <div className="flex items-center gap-1">
          <Button variant="outline" size="icon" className="h-8 w-8" disabled={!hasPrevious} onClick={() => goToPage(1)}>
            <ChevronsLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="icon" className="h-8 w-8" disabled={!hasPrevious} onClick={() => goToPage(page - 1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Select value={page.toString()} onValueChange={(v) => goToPage(Number(v))}>
            <SelectTrigger className="w-16 h-8!">
              <SelectValue />
            </SelectTrigger>
            <SelectContent position="popper" side="top" className="max-h-50">
              {availablePages.map((p) => (
                <SelectItem key={p} value={p.toString()}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" className="h-8 w-8" disabled={!hasNext} onClick={() => goToPage(page + 1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="icon" className="h-8 w-8" disabled={!hasNext} onClick={() => goToPage(totalPages)}>
            <ChevronsRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </>
  )
}
